// @ts-strict-ignore
import { createContext } from "react";

import type {
  Brand,
  CatalogSearchType,
  CategoryFilter,
  ConditionFilter,
  Filter,
  IngredientFilter,
  IngredientFilterV2,
  OptimizedTaxonomyFilter,
  ProductSortDirection,
  ProductSortField,
  RelevanceFilter,
  SearchEntrypoint,
  SupplementTypeFilter,
  TaxonomyFilter,
} from "@shared/components/CatalogSearch/CatalogSearch";
import type { RecentSearchesItem } from "@shared/data/queries/RecentSearchesQuery/RecentSearches.query";
import type { EqpLevelType } from "@shared/types/graphqlGenerated";
import { type Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

interface CatalogSearchData {
  activeFilters: Filter[];
  setActiveFilters: (activeFilters: Filter[]) => void;
  activeBrands: Brand[];
  setActiveBrands: (
    activeBrands: Brand[],
    clearExistingFilters?: boolean,
    searchEntrypoint?: SearchEntrypoint
  ) => void;
  activeIngredients: IngredientFilter[] | IngredientFilterV2[];
  setActiveIngredients: (activeIngredients: IngredientFilter[] | IngredientFilterV2[]) => void;
  excludedIngredients: IngredientFilter[] | IngredientFilterV2[];
  setExcludedIngredients: (excludedIngredients: IngredientFilter[] | IngredientFilterV2[]) => void;
  activeSupplementTypes: SupplementTypeFilter[];
  setActiveSupplementTypes: (activeSupplementTypes: SupplementTypeFilter[]) => void;
  activeConditions?: ConditionFilter[];
  setActiveConditions?: (activeConditions: ConditionFilter[]) => void;
  activeCategories?: CategoryFilter[];
  setActiveCategories?: (activeCategories: CategoryFilter[]) => void;
  activeEqpLevels?: EqpLevelType[];
  setActiveEqpLevels?: (activeEqpLevels: EqpLevelType[]) => void;
  activeTaxonomies?: TaxonomyFilter[];
  activeOptimizedTaxonomies?: OptimizedTaxonomyFilter[];
  setActiveTaxonomies?: (activeTaxonomies: TaxonomyFilter[]) => void;
  setActiveOptimizedTaxonomies?: (
    activeOptimizedTaxonomies: OptimizedTaxonomyFilter[],
    clearAllPriorSearchParams?: boolean
  ) => void;
  searchQuery: string;
  setSearchQuery: (queryText: string, queryId?: string, clearAllPriorSearchParam?: boolean) => void;
  removeActiveFilter: (filterToRemove: Filter) => void;
  activeSearchType: CatalogSearchType;
  setActiveSearchType: (searchFilter: CatalogSearchType) => void;
  sortDirection: ProductSortDirection;
  sortField: ProductSortField;
  setSort: (sortField: ProductSortField, sortDirection: ProductSortDirection) => void;
  isOnlyShowFavorites?: boolean;
  setIsOnlyShowFavorites?: (value: boolean) => void;
  isOnlyShowEmersonNew?: boolean;
  setIsOnlyShowEmersonNew?: (value: boolean) => void;
  isOnlyShowInStock?: boolean;
  setIsOnlyShowInStock?: (value: boolean) => void;
  isGridView?: boolean;
  setGridView?: (value: boolean) => void;
  onlyIngredient: boolean;
  setOnlyIngredient: (value: boolean, areIngredientsExcluded?: boolean) => void;
  typeaheadSelectionId: string;
  typeaheadSelectionName?: string;
  setTypeaheadAsFilter: (
    value: Pick<TypeaheadType, "id" | "name" | "entityType">,
    initFilters?: {},
    clearAllPriorSearchParam?: boolean
  ) => void;
  activeRelevanceFilters?: RelevanceFilter[];
  setActiveRelevanceFilters?: (value: RelevanceFilter[]) => void;
  setClickedRecentSearchTypeaheadToParam?: (recentSearch: RecentSearchesItem) => void;
  isFsTested?: boolean;
  setIsFsTested?: (value: boolean) => void;
  searchSessionEntrypoint?: SearchEntrypoint;
  setSearchSessionEntrypoint?: (value: SearchEntrypoint) => void;
  searchStartedFromBrand?: boolean;
}

const CatalogSearchContext = createContext<CatalogSearchData>(null);

export type { CatalogSearchData };
export { CatalogSearchContext };
